import { isClientLocalSettingsPath } from "../../components/settings/feedbackSettings.logic";
import { isPairingPath } from "./browserPairing.logic";
import type { ConnectAccountStatus } from "@afar-research/contracts";

interface BrowserEnvironment {
  readonly VITE_AFAR_RESEARCH_WEB_ONLY?: string;
  readonly VITE_AFAR_RESEARCH_RUNTIME_WEB?: string;
  readonly VITE_AFAR_CONNECT_ORIGIN?: string;
}

/**
 * Only the deployed Runtime Web Client build sets this. A Desktop Application Release builds the
 * same bundle with the same stage and Connect configuration, and its Environment serves it as the
 * Host Web Client, so neither of those can tell the two apart (#1019).
 */
export function isRuntimeWebBuild(env: BrowserEnvironment): boolean {
  return env.VITE_AFAR_RESEARCH_RUNTIME_WEB === "1";
}

/** Only a Runtime Web Client build admits people by account. */
export function requiresBrowserAccount(env: BrowserEnvironment): boolean {
  return isRuntimeWebBuild(env);
}

/**
 * Browser sign-in returns to `/connect-callback.html` on the page's own origin, which Connect
 * accepts only for the Runtime Web Client's registered origin; an Environment-served origin never
 * is. The Host Web Client authorizes through pairing instead, and manages the account in Desktop.
 */
export function offersBrowserAccount(env: BrowserEnvironment): boolean {
  return Boolean(env.VITE_AFAR_CONNECT_ORIGIN) && isRuntimeWebBuild(env);
}

/** The Runtime Web Client benchmark's build has no primary Environment to find or pair with. */
export function isWebOnlyDevelopment(env: BrowserEnvironment): boolean {
  return env.VITE_AFAR_RESEARCH_WEB_ONLY === "1";
}

export async function resolveBrowserEntry(input: {
  readonly isElectron: boolean;
  readonly env: BrowserEnvironment;
  readonly pathname: string;
  readonly readSession: () => Promise<{ readonly authenticated: boolean }>;
  readonly readAccount?: () => Promise<ConnectAccountStatus>;
}): Promise<"app" | "pair" | "sign-in"> {
  if (input.isElectron) return "app";
  if (requiresBrowserAccount(input.env)) {
    try {
      if ((await input.readAccount?.())?.state !== "signed-in") return "sign-in";
    } catch {
      return "sign-in";
    }
  }
  if (isWebOnlyDevelopment(input.env) && !requiresBrowserAccount(input.env)) return "app";
  if (isPairingPath(input.pathname)) return "pair";
  if (isClientLocalSettingsPath(offersBrowserAccount(input.env), input.pathname)) return "app";
  try {
    return (await input.readSession()).authenticated ? "app" : "pair";
  } catch {
    // The app's connection state reports an unreachable Environment.
    return "app";
  }
}
